import React from 'react';
import { Input, message } from 'antd';
import dayjs from 'dayjs';
import { useAuth } from '../../contexts/AppContext';
import ImageUploader from '../../components/utils/ImageUploader'; // single-image uploader

const { TextArea } = Input;

/**
 * AdvDetailTab edits advertisement basics used by TsAdvEdit.
 * Includes name, alias, main image, and specials fields.
 */
const AdvDetailTab = ({ formData, onChange, onNested, token }) => {
  const { userInfo } = useAuth();
  if (!formData) return null;

  const mainImage = formData.image;
  const special = formData.specials?.[0] || {};

  const prefix = userInfo?.user?.S3Id ? `full/${userInfo.user.S3Id}/` : undefined;
  const thumbPrefix = userInfo?.user?.S3Id ? `thumb/${userInfo.user.S3Id}/` : undefined;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
      {/* Left side: basic info */}
      <div className="space-y-6">
        {/* Name */}
        <div>
          <label className="block font-medium mb-1">Name</label>
          <Input
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="Enter advertisement name"
          />
        </div>

        {/* Alias */}
        <div>
          <label className="block font-medium mb-1">Alias</label>
          <TextArea
            value={formData.alias}
            onChange={(e) => onChange('alias', e.target.value)}
            placeholder="Optional alias"
            rows={2}
          />
        </div>

        {/* Main Image */}
        <div>
          <label className="block font-medium mb-1">Main Image</label>
          {mainImage && (
            <img
              src={mainImage}
              alt="Main"
              className="w-full rounded-lg mb-2 border"
              style={{ maxHeight: 180, objectFit: 'cover' }}
            />
          )}
          <ImageUploader
            token={token}
            width={1080}
            height={340}
            prefix={prefix}
            thumbPrefix={thumbPrefix}
            onChange={(url) => onChange('image', url)}
          />
        </div>
      </div>

      {/* Right side: specials */}
      <div className="space-y-6">
        {/* Title and clear button */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Specials</h3>
          <button
            type="button"
            className=" text-red-500 hover:underline text-sm"
            onClick={() => onChange('specials', [])}
          >
            Clear Specials
          </button>
        </div>

        {/* Start Time */}
        <div>
          <label className="block font-medium mb-1">Start Time</label>
          <Input
            type="datetime-local"
            value={
              special.startDatetime
                ? dayjs(special.startDatetime).format('YYYY-MM-DDTHH:mm')
                : ''
            }

            onChange={(e) => {
              const newStart = e.target.value;
              const end = special.endDatetime;

              // Start time cannot be later than the end time
              if (end && newStart && dayjs(newStart).isAfter(dayjs(end))) {
                message.error('Start time cannot be later than end time.');
                return;
              }

              onNested('specials', '0', {
                ...special,
                startDatetime: newStart,
              });
            }}
          />
        </div>

        {/* End Time */}
        <div>
          <label className="block font-medium mb-1">End Time</label>
          <Input
            type="datetime-local"
            value={
              special.endDatetime
                ? dayjs(special.endDatetime).format('YYYY-MM-DDTHH:mm')
                : ''
            }

            onChange={(e) => {
              const newEnd = e.target.value;
              const start = special.startDatetime;

              // End time cannot be earlier than the start time
              if (start && newEnd && dayjs(newEnd).isBefore(dayjs(start))) {
                message.error('End time cannot be earlier than start time.');
                return;
              }

              onNested('specials', '0', {
                ...special,     
                endDatetime: newEnd, 
              });
            }}
          />
        </div>

        {/* Special Image */}
        <div>
          <label className="block font-medium mb-1">Special Image</label>
          {special.image && (
            <img
              src={special.image}
              alt="Special"
              className="w-full rounded-lg mb-2 border"
              style={{ maxHeight: 180, objectFit: 'cover' }}
            />
          )}
          <ImageUploader
            token={token}
            width={1080}
            height={340}
            prefix={prefix}
            thumbPrefix={thumbPrefix}
            onChange={(url) =>
              onNested('specials', '0', {
                ...special,
                image: url,
              })
            }
          />
        </div>
      </div>
    </div>
  );
};

export default AdvDetailTab;
